
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { Route } from 'vue-router';
import { namespace } from 'vuex-class';

import type { WebLocale } from '@/enums/web-locale';
import { Locale } from '@/helpers/locale-helper';
import { SingleStepContext } from '@/helpers/sports/config';
import { displayTimeZone } from '@/helpers/time-zone-helper';
import { SnowplowSportContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SportPageType } from '@/interfaces/sport-graphql';

const Language = namespace('language');
const User = namespace('user');
const Routing = namespace('routing');
let isMounted = false;

@Component({
	name: 'TimeZoneSwitcher',
	components: {},
	apollo: {},
})
export default class TimeZoneSwitcher extends Vue {
	@Prop({ required: true }) context: SingleStepContext;

	@Language.State locales: Locale[];
	@Language.State webLocale: WebLocale;
	@Language.Getter language: string;
	@Language.Getter locale: string;

	@User.State timeZone: string | undefined;
	@User.Action setTimeZone: (value: string) => Promise<void>;

	@Routing.State activeRoute: Route;

	visible = false;

	mounted() {
		this.visible = this.localeDefaultAndBrowserMismatch && !this.userHasSetTimeZone;
		if (!isMounted && this.visible) {
			TrackingHelper.trackEvent('timezone_switcher', { action: 'impression' }, this.sportContexts);
			isMounted = true;
		}
	}

	changeTimeZone() {
		this.setTimeZone(this.browserTimeZone);
		this.visible = false;
		TrackingHelper.trackEvent('userinteraction', { action: 'timezone_switcher_click' }, this.sportContexts);
	}

	get sportContexts() {
		let sportType = this.context.sportType as string;
		if ((this.context.pageType as SportPageType) === SportPageType.AllSports) {
			sportType = 'GENERIC';
		}

		return [new SnowplowSportContext(sportType, this.context.pageType)];
	}

	get localeDefaultTimeZone() {
		return this.currentLocale?.timezone;
	}

	get currentLocale() {
		return this.locales.find(locale => locale.webLocale === this.webLocale);
	}

	get browserTimeZone() {
		return Intl.DateTimeFormat().resolvedOptions().timeZone;
	}

	get localeDefaultAndBrowserMismatch() {
		return this.localeDefaultTimeZone != this.browserTimeZone;
	}

	get userHasSetTimeZone() {
		return !!this.timeZone;
	}

	get currentTimeZone() {
		return displayTimeZone(this.locales, this.webLocale, this.timeZone)?.replaceAll('_', ' ');
	}

	get localeDefaultTimeZoneAbbreviation() {
		return this.currentLocale?.timezone_abbreviation;
	}
}
